/**
 * RealGrid 2.8.4 공통 유틸 (eKMTC)
 *
 * 페이지마다 반복되는 RealGrid 초기화 boilerplate
 * (Provider/GridView 생성, Fields/Columns/Layout 설정, 헤더/푸터/체크바 등 setter 호출) 을
 * `createGrid()` 한 번에 처리한다.
 *
 * 의존: realgrid@2.8.4
 *
 * 호출처 예시 — `src/pages/trans/WorkingProgressExport.vue`
 *
 * @example
 *  import { createGrid, destroyGrid, setColumnsWithLayout } from '@/plugins/realgridUtil'
 *
 *  // mounted()
 *  const { gridView, provider } = createGrid('realgrid', {
 *    fields,                             // 데이터 필드 정의
 *    columns: columns1,                  // 컬럼 정의 (렌더러 포함)
 *    layout: layout1,                    // 컬럼 그룹 레이아웃
 *    options:      { ... },              // gridView.setOptions() 일괄 설정
 *    footer:       { visible: false },   // 푸터 숨김
 *    footers:      { ... },              // 다중 푸터 (setFooters)
 *    rowIndicator: { visible: false },   // 좌측 인디케이터 숨김
 *    stateBar:     { visible: false },   // 좌측 상태바 숨김
 *    header:       { height: 80 },       // 헤더 행 높이
 *    displayOptions: {
 *      rowHeight: 70,                    // 데이터 행 높이
 *      fitStyle:  'evenFill'             // 컬럼 너비를 그리드 너비에 맞춤
 *    },
 *    copyOptions: {
 *      copyDisplayText: true,            // 표시 텍스트 그대로 복사
 *      lookupDisplay:   true,            // lookup 컬럼은 label 복사
 *      copyTextCallback: this.copyTextCallback
 *    }
 *  })
 *  this.gridView = gridView
 *  this.provider = provider
 *
 *  // beforeDestroy()
 *  destroyGrid(this.gridView, this.provider)
 */

import { GridView, LocalDataProvider } from 'realgrid'

export const createGrid = (domId, options = {}) => {
  if (!domId) throw new Error('[realgridUtil] domId 가 필요합니다.')

  const provider = new LocalDataProvider(true)
  const gridView = new GridView(domId)
  gridView.setDataSource(provider)
  if (options.fields) provider.setFields(options.fields)

  // 반드시 setColumns 먼저, setColumnLayout 나중 (RealGrid 권장 순서).
  // 반대로 호출하면 컬럼 너비 설정이 일부 누락되는 증상이 있음.
  if (options.columns) gridView.setColumns(options.columns)
  if (options.layout) gridView.setColumnLayout(options.layout)
  applyGridOptions(gridView, options)

  return { gridView, provider }
}

export const setColumnsWithLayout = (gridView, columns, layout) => {
  if (!gridView) return
  gridView.setColumns(columns)
  if (layout) gridView.setColumnLayout(layout)
}

export const destroyGrid = (gridView, provider) => {
  if (gridView && typeof gridView.destroy === 'function') gridView.destroy()
  if (provider && typeof provider.clearRows === 'function') provider.clearRows()
  if (provider && typeof provider.destroy === 'function') provider.destroy()
}

const applyGridOptions = (gridView, opts) => {
  if (!opts) return
  if (opts.options) gridView.setOptions(opts.options)
  if (opts.header) Object.assign(gridView.header, opts.header)
  if (opts.footer) gridView.setFooter(opts.footer)
  if (opts.footers) gridView.setFooters(opts.footers)
  if (opts.rowIndicator) gridView.setRowIndicator(opts.rowIndicator)
  if (opts.stateBar) gridView.setStateBar(opts.stateBar)
  if (opts.checkBar) gridView.setCheckBar(opts.checkBar)
  if (opts.fixedOptions) gridView.setFixedOptions(opts.fixedOptions)
  if (opts.editOptions) gridView.setEditOptions(opts.editOptions)
  if (opts.copyOptions) gridView.setCopyOptions(opts.copyOptions)
  if (opts.pasteOptions) gridView.setPasteOptions(opts.pasteOptions)
  if (opts.sortingOptions) gridView.setSortingOptions(opts.sortingOptions)
  if (opts.filteringOptions) gridView.setFilteringOptions(opts.filteringOptions)
  if (opts.displayOptions) Object.assign(gridView.displayOptions, opts.displayOptions)
}
